import { Button, Card, Col, Input, Popover, Row, Space } from "antd";
import { Typography } from "antd/es";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { ReactNode } from "react-markdown/lib/react-markdown";
import Icon from "../../../assets/icons/icon";
import MotorAtlasLink from "../../../components/motorAtlasLink";
import _t from "../../../lang/translate";
import { ModelMatch, SourceVehiclePreview } from "../../../types/motorAtlasCatalogTypes";
import format from "../../../utilities/formatNumbers";
import { constructStringFromKeys, extractTokens, normalize } from "../../../utilities/stringExtraFunctions";
import { TinySpace, Value, VehicleSpecCol } from "./components";
import { formatProductionYears } from "./util";



type Props = {
  sourceVehicle: SourceVehiclePreview | null
  matches: ModelMatch[] | null
  selectedMatch: ModelMatch | null;
  setSelectedMatch: React.Dispatch<React.SetStateAction<ModelMatch | null>>
}
export default function MotorAtlasCatalogMatches({ matches, selectedMatch, setSelectedMatch, sourceVehicle }: Props) {
  const [viewModelDetails, setViewModelDetails] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const [bestMatch, setBestMatch] = useState<BestMatch | null>(null)
  const [scores, setScores] = useState<Map<string, BestMatch> | null>(null)

  const toggleViewDetails = (match: ModelMatch) => setViewModelDetails((prev) => {
    const set = new Set(prev)
    const included = set.has(match.eCode ?? '');
    if (included) {
      set.delete(match.eCode ?? '')
    } else {
      set.add(match.eCode ?? '')
    }
    return set
  })

  useEffect(() => {
    if (sourceVehicle && matches) {
      const dmr = sourceVehicle.supplementary.dmr;
      const [best, scoresMap] = findBestMatch({
        driveType: sourceVehicle?.technical?.driveType ?? dmr?.technical?.driveType ?? "",
        firstRegDate: sourceVehicle?.core?.firstRegDate ?? dmr?.core?.firstRegDate ?? "",
        make: sourceVehicle?.core?.make ?? dmr?.core?.make ?? "",
        batteryCapacity: sourceVehicle?.technical?.batteryCapacity ?? dmr?.technical?.batteryCapacity ?? "",
        bodyType: sourceVehicle?.technical?.bodyType ?? dmr?.technical?.bodyType ?? "",
        co2Emission: sourceVehicle?.technical?.co2Emission ?? dmr?.technical?.co2Emission ?? "",
        fuelType: sourceVehicle?.technical?.fuelType ?? dmr?.technical?.fuelType ?? "",
        gearbox: sourceVehicle?.technical?.gearbox ?? dmr?.technical?.gearbox ?? "",
        horsePower: sourceVehicle?.technical?.horsePower ?? dmr?.technical?.horsePower ?? "",
        model: sourceVehicle?.core?.model ?? dmr?.core.model ?? "",
        subModel: sourceVehicle?.core?.subModel ?? dmr?.core?.subModel ?? "",
        variant: sourceVehicle?.core?.subModel ?? dmr?.core?.subModel ?? ""
      }, matches)
      setBestMatch(best)
      setScores(scoresMap)
    }
    // Check if the previous selection is still in the list, then keep the selection, else reset to null.
    if (!matches) return;
    setSelectedMatch(prev => matches?.some(m => m.eCode === prev?.eCode) ? prev : null)
  }, [matches])

  useEffect(() => {
    if (!matches) return;
    if (!!selectedMatch) return;
    if (sourceVehicle?.supplementary.potentialECodes && sourceVehicle.recommendedFilters?.eCode) {
      const match = matches.find(m => m.eCode === sourceVehicle.recommendedFilters?.eCode)
      if (match) {
        setSelectedMatch(match)
      }
    }
  }, [selectedMatch, matches])

  const dedupedMatches = useMemo(() => {
    if (!matches) return []
    const seen = new Set()
    const unique = [];
    for (const m of matches) {
      if (!seen.has(m.eCode)) {
        seen.add(m.eCode)
        unique.push(m)
      }
    }
    return unique
  }, [matches])

  const normMatches = useMemo(() => {
    return dedupedMatches?.map((item, index) => ({
      index: index,
      all: new Set(
        normalize(`${item.make} ${item.model} ${item.subModel} ${item.variant} ${item.marketDescription}`)
          .split(/\s+/)
          .filter(Boolean)
      ),
    })) ?? []
  }, [dedupedMatches])



  const filteredMatches = useMemo(() => {
    if (!search.trim()) return dedupedMatches
    const parts = normalize(search).split(" ")
    const filteredNormMatches = normMatches.filter(item => {
      return parts.every(token => item.all.has(token))
    })
    const filtered = filteredNormMatches?.map((item) => dedupedMatches?.[item.index]).filter(Boolean) as ModelMatch[]
    return filtered
  }, [search, dedupedMatches])

  const sortedMatches = useMemo(() => {
    if (!filteredMatches) return [];
    return filteredMatches.toSorted((a, b) => {
      return (scores?.get(b.eCode)?.score ?? -Infinity) - (scores?.get(a.eCode)?.score ?? -Infinity)
    })
  }, [filteredMatches, scores])

  return (
    <div style={{ maxHeight: "100%", overflow: "auto" }}>
      <h3 className="mt-05">
        Resultater: {sortedMatches.length}
      </h3>
      <div>
        <Input className="mb-1 mt-05"
          value={search}
          onChange={e => setSearch(e.target.value)}
          suffix={<Icon name="search" />}
          placeholder="Søg..." />
      </div>
      <Row>
        {sortedMatches?.map((match, index) => {
          const isSelected = match.eCode === selectedMatch?.eCode
          return (
            <Col key={match.eCode} span={24}>
              <Card size="small"
                bodyStyle={{ padding: 0, overflow: "hidden" }}
                className={`mb-1 transition model-match-card
                  ${isSelected ? "selected" : ""}
                  ${bestMatch?.match?.eCode === match.eCode ? "best-match" : ""}`}>

                <Row justify="space-between"
                  style={{ padding: 8, }}
                  className="clickable-card"
                  onClick={() => setSelectedMatch(match)}
                  gutter={[8, 8]} wrap={false} align="bottom">

                  <Col style={{ minWidth: 0, maxWidth: "100%", height: "100%" }}>
                    <Typography.Paragraph className="mb-03" ellipsis style={{ wordBreak: "keep-all" }}>
                      <h4 className="m-0" style={{ fontSize: 13, display: "inline" }}>
                        {match.make}
                      </h4>
                      {' '}•{' '}
                      <h4 className="m-0" style={{ fontSize: 13, display: "inline" }}>
                        {match.marketDescription}
                      </h4>
                      {/* {' '}•{' '} */}
                      {/* <span style={{ wordBreak: "keep-all" }}> */}
                      {/*   {match.variant} */}
                      {/* </span> */}
                    </Typography.Paragraph>
                    <Space size={"middle"}>
                      <TinySpace tooltip={_t('fuel')}>
                        <Icon name="gas-pump" size="large" color="#B37FEB" />
                        <Value value={match.fuel} />
                      </TinySpace>
                      <TinySpace tooltip={_t('production_years')}>
                        <Icon name="calendar-blank" size="large" color="#B37FEB" />
                        <Value value={formatProductionYears(match.productionYears)} />
                      </TinySpace>
                      <TinySpace tooltip={_t('horse_power')}>
                        <Icon name="gauge" size="large" color="#B37FEB" />
                        <Value value={match.horsePower} suffix={"hk"} />
                      </TinySpace>
                      <TinySpace tooltip={_t('trim')}>
                        <Icon name="car-simple" size="large" color="#389E0F" />
                        <Value value={match.body} />
                      </TinySpace>
                      {match.recommendedPrice && (
                        <TinySpace tooltip={_t('new_price')}>
                          <Icon name="currency-circle-dollar" size="large" color="#0299FB" />
                          <Value value={
                            <Typography.Paragraph ellipsis style={{ maxWidth: "100%" }} className="m-0 recommended-price">
                              {format.price(match.recommendedPrice)}
                            </Typography.Paragraph>

                          } />
                        </TinySpace>
                      )}
                    </Space>
                  </Col>
                  <Col style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    gap: 4,

                  }}>

                    {scores?.get(match?.eCode) && (
                      <PopoverScoreDetails bestMatch={scores.get(match.eCode)}>
                        <span> {format.percent(scores.get(match.eCode)?.score)} </span>
                      </PopoverScoreDetails>
                    )}

                    <Button size="small" onClick={(e) => {
                      e.stopPropagation();
                      toggleViewDetails(match);
                    }}
                      style={{ marginRight: -7 }}
                      className="btn-text-primary-hover"
                      type="text"
                      icon={<Icon name={viewModelDetails.has(match.eCode) ? "eye-off-outline" : "eye-outline"} />}
                    >
                      Se detaljer
                    </Button>
                  </Col>
                </Row>

                {viewModelDetails.has(match.eCode ?? '') && (
                  <div style={{ padding: 12 }} >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <h4>Bildata</h4>
                      <MotorAtlasLink motorAtlasId={match.eCode} firstRegDate={sourceVehicle?.core.firstRegDate} />
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
                      <TinySpace tooltip={_t('brand')}>
                        <Icon name="buildings" size="large" color="#0299FB" />
                        <Value value={match.make} />
                      </TinySpace>
                      <TinySpace tooltip={_t('model')}>
                        <Icon name="car" size="large" color="#0299FB" />
                        <Value value={match.model} />
                      </TinySpace>
                      <TinySpace tooltip={_t('subModel')}>
                        <Icon name="package" size="large" color="#0299FB" />
                        <Value value={match.subModel} />
                      </TinySpace>
                      <TinySpace tooltip={_t('variant')}>
                        <Icon name="tag" size="large" color="#0299FB" />
                        <Value value={match.variant} />
                      </TinySpace>
                      <TinySpace tooltip={_t('description')}>
                        <Icon name="file-text" size="large" color="#0299FB" />
                        <Value value={match.marketDescription} />
                      </TinySpace>
                    </div>
                    <h4 className="mt-1">{_t("technical_data")}</h4>
                    <Row wrap justify="space-between" gutter={[24, 16]}>
                      <VehicleSpecCol>
                        <TinySpace tooltip={_t('production_years')}>
                          <Icon name="calendar-blank" size="large" color="#B37FEB" />
                          <Value value={formatProductionYears(match.productionYears)} />
                        </TinySpace>
                        <TinySpace tooltip={_t('drive_wheel')}>
                          <Icon name="git-commit" size="large" color="#B37FEB" />
                          <Value value={match.drive} />
                        </TinySpace>
                      </VehicleSpecCol>
                      <VehicleSpecCol>
                        <TinySpace tooltip={_t('fuel')}>
                          <Icon name="gas-pump" size="large" color="#B37FEB" />
                          <Value value={match.fuel} />
                        </TinySpace>
                        <TinySpace tooltip={_t('gearbox')}>
                          <Icon name="gear" size="large" color="#B37FEB" />
                          <Value value={match.transmission} />
                        </TinySpace>
                      </VehicleSpecCol>
                      <VehicleSpecCol>
                        <TinySpace tooltip={_t('engine_size')}>
                          <Icon name="wrench" size="large" color="#B37FEB" />
                          <Value value={match.engineSize} />
                        </TinySpace>
                        <TinySpace tooltip={_t('horse_power')}>
                          <Icon name="gauge" size="large" color="#B37FEB" />
                          <Value value={match.horsePower} suffix={"hk"} />
                        </TinySpace>
                      </VehicleSpecCol>
                    </Row>
                    <h4 className="mt-1">{_t("environment", "physical", "/")}</h4>
                    <Row wrap justify="space-between" gutter={[24, 16]}>
                      <VehicleSpecCol>
                        <TinySpace tooltip={_t('trim')}>
                          <Icon name="car-simple" size="large" color="#389E0F" />
                          <Value value={match.body} />
                        </TinySpace>
                        <TinySpace tooltip={_t("battery_capacity")}>
                          <Icon name="battery-full" size="large" color="#389E0F" />
                          <Value value={match.batterySize} suffix={"kWh"} />
                        </TinySpace>
                      </VehicleSpecCol>
                      <VehicleSpecCol>
                        <TinySpace tooltip={_t('doors')}>
                          <Icon name="door" size="large" color="#389E0F" />
                          <Value value={match.doors} />
                        </TinySpace>
                        <TinySpace tooltip="Rækkevidde min.">
                          <Icon name="crosshair-simple" size="large" color="#389E0F" />
                          <Value value={match.electricRangeMin} suffix={"km"} />
                        </TinySpace>
                      </VehicleSpecCol>
                      <VehicleSpecCol>
                        <TinySpace tooltip="CO2">
                          <Icon name="leaf" size="large" color="#389E0F" />
                          <Value prefix={"ca."} value={match.emission} suffix={"g/km"} />
                        </TinySpace>
                        <TinySpace tooltip="Rækkevidde max.">
                          <Icon name="crosshair" size="large" color="#389E0F" />
                          <Value value={match.electricRangeMax} suffix={"km"} />
                        </TinySpace>
                      </VehicleSpecCol>
                    </Row>
                  </div>
                )}
              </Card>
            </Col>
          )
        })}
      </Row>
    </div>
  )
}


function jaccard(a: string[], b: string[]) {
  const A = new Set(a), B = new Set(b);
  const intersection = A.intersection(B).size
  const union = A.union(B).size;
  return union ? intersection / union : 0;
}

const weight = {
  jaccard: 0.45,
  horsePower: 0.15,
  driveType: 0.10,
  fuelType: 0.10,
  bodyType: 0.04,
  year: 0.08,
  gearbox: 0.03,
  batteryCapacity: 0.05,
  // emission: 0.10,
};

function scoreYear(srcFirstRegDate: string, matchProductionYears: string[]) {
  const year = moment(srcFirstRegDate).year()
  const prodYears = matchProductionYears.map(Number)
  const firstProdYear = prodYears.shift()
  const lastProdYear = prodYears.pop()
  if (firstProdYear && lastProdYear && (year >= firstProdYear || year <= lastProdYear)) {
    return 1
  }
  return 0
}
function scoreHorsePower(srcHorsePower: string, matchHorsePower: string) {
  const srcHP = safeNumber(srcHorsePower)
  const matchKW = safeNumber(matchHorsePower)
  const matchHP = matchKW * 1.341
  const differenceHP = Math.abs(srcHP - matchHP)
  const differenceKW = Math.abs(srcHP - matchKW)
  return Math.max(Math.exp(-differenceHP / 30), Math.exp(-differenceKW / 22)) // Scale of 30 horsepower or the equivalent 22 KW
}
function scoreFuelType(srcFuelType: string, matchFuelType: string) {
  if (normalize(srcFuelType) === normalize(matchFuelType)) return 1
  return 0
}
function scoreBodyType(srcBodyType: string, matchBodyType: string) {
  if (normalize(srcBodyType) === normalize(matchBodyType)) return 1
  return 0
}
function scoreDriveType(srcDriveType: string, matchDriveType: string) {
  if (normalize(srcDriveType) === normalize(matchDriveType)) return 1
  return 0
}
function scoreGearbox(srcGearbox: string, matchGearbox: string) {
  if (normalize(srcGearbox) === normalize(matchGearbox)) return 1
  return 0
}
const safeNumber = (num: unknown) => {
  const number = Number(num)
  if (isNaN(number)) {
    return 0
  }
  return number
}
function scoreBatteryCapacity(srcBattery: string | undefined | null, matchBattery: string | null) {
  const src = safeNumber(srcBattery) // null = 0
  const match = safeNumber(matchBattery)
  if ((src === 0) !== (match === 0)) return 0

  const difference = Math.abs(src - match)
  return Math.exp(-difference / 5) // Scale of 5
}
function scoreEmission(srcEmission: string | undefined | null, matchEmission: string | null) {
  if (normalize(matchEmission ?? '') === '-' && srcEmission === '0') return 1;
  const src = safeNumber(srcEmission) // null = 0
  const match = safeNumber(matchEmission)
  if ((src === 0) !== (match === 0)) return 0
  const difference = Math.abs(src - match)
  // Takes the natural exponent of the negative difference, which will always be a number between 0-1. Math.exp(0) is 1;
  return Math.exp(-difference / 10) // Scale of 10
}


type Score = {
  score: number,
  weightedScore: number;
}
type ScoreProperties = "jac" | "year" | "horsePower" | "fuelType" | "bodyType" | "gearbox" | "batteryCapacity" // | "emission"
type ScoreResult = {
  scores: Record<ScoreProperties, Score>
  totalScore: number;
}
function scoreMatch(src: SourceVehicle, match: ModelMatch): ScoreResult {
  const srcTokens = extractTokens(constructStringFromKeys(src, ['model', 'subModel', 'variant']))
  const matchTokens = extractTokens(constructStringFromKeys(match, ['model', 'subModel', 'variant']))
  const jac = jaccard(srcTokens, matchTokens)

  const year = scoreYear(src.firstRegDate || '', match.productionYears)
  const horsePower = scoreHorsePower(src.horsePower, match.horsePower)
  const fuelType = scoreFuelType(src.fuelType, match.fuel)
  const bodyType = scoreBodyType(src.bodyType, match.body)
  const driveType = scoreDriveType(src.driveType, match.drive)
  const gearbox = scoreGearbox(src.gearbox, match.transmission)
  const batteryCapacity = scoreBatteryCapacity(src.batteryCapacity, match.batterySize)
  const emission = scoreEmission(src.co2Emission, match.emission)


  const scores = {
    jac: {
      score: jac,
      weightedScore: jac * weight.jaccard
    },
    year: {
      weightedScore: year * weight.year,
      score: year,
    },
    horsePower: {
      weightedScore: horsePower * weight.horsePower,
      score: horsePower
    },
    fuelType: {
      weightedScore: fuelType * weight.fuelType,
      score: fuelType
    },
    bodyType: {
      weightedScore: bodyType * weight.bodyType,
      score: bodyType,
    },
    driveType: {
      weightedScore: driveType * weight.driveType,
      score: driveType,
    },
    gearbox: {
      weightedScore: gearbox * weight.gearbox,
      score: gearbox
    },
    batteryCapacity: {
      weightedScore: batteryCapacity * weight.batteryCapacity,
      score: batteryCapacity,
    },
    // emission: {
    //   weightedScore: emission * weight.emission,
    //   score: emission
    // },
  }


  const weightedScore = scores.jac.weightedScore
    + scores.year.weightedScore
    + scores.horsePower.weightedScore
    + scores.fuelType.weightedScore
    + scores.bodyType.weightedScore
    + scores.driveType.weightedScore
    + scores.gearbox.weightedScore
    + scores.batteryCapacity.weightedScore
  // + scores.emission.weightedScore

  const res = {
    scores: scores,
    totalScore: weightedScore,
  }
  return res;
}

type BestMatch = {
  match: ModelMatch | null,
  score: number,
  result: null | ScoreResult
}
type SourceVehicle = {
  make: string;
  model: string;
  subModel: string;
  variant: string;
  fuelType: string;
  driveType: string;
  bodyType: string;
  firstRegDate: string;
  horsePower: string;
  gearbox: string;
  co2Emission: string;
  batteryCapacity: string;
}
function findBestMatch(src: SourceVehicle, matches: ModelMatch[]): [BestMatch, Map<string, BestMatch>] {
  const blocked = matches.filter(match => {
    const make = src.make ?? "";
    const fuelType = src.fuelType ?? "";
    const driveType = src.driveType ?? "";
    const firstRegDate = src.firstRegDate ?? "";
    if (make && normalize(make) !== normalize(match.make)) return false;
    if (fuelType && normalize(fuelType) !== normalize(match.fuel)) return false;
    if (driveType && normalize(driveType) !== normalize(match.drive)) return false;
    if (firstRegDate) {
      match.productionYears.toSorted()
      const fromYear = match.productionYears[0]
      const toYear = match.productionYears[match.productionYears.length - 1]
      const year = moment(firstRegDate).year()
      const from = parseInt(fromYear)
      const to = parseInt(toYear)
      if (from && to && !(year >= from && year <= to)) return false;
    }
    return true;
  });

  let best: BestMatch = {
    match: null as ModelMatch | null,
    score: -1,
    result: null
  };
  const scoresMap = new Map<string, BestMatch>()
  for (const match of (blocked.length ? blocked : matches)) {
    const score = scoreMatch(src, match);
    scoresMap.set(match.eCode, {
      match: match,
      score: score.totalScore,
      result: score,
    })
    if (score.totalScore > best.score) {
      best = {
        match: match,
        score: score.totalScore,
        result: score,
      }
    }
  }
  return [best, scoresMap];
}

type PopoverScoreDetailsProps = {
  children: ReactNode
  bestMatch: undefined | null | BestMatch
}
function PopoverScoreDetails({ bestMatch, children }: PopoverScoreDetailsProps) {
  if (!bestMatch) return null;
  return (
    <Popover
      overlayInnerStyle={{
        width: "auto",
        maxWidth: 500
      }}
      title={
        <Row justify="space-between">
          <Col>
            <h4 className="mb-0">Score:</h4>
          </Col>
          <Col>
            <span style={{ width: 50 }}>{format.percent(bestMatch.result?.totalScore)}</span>
          </Col>
        </Row>
      }
      content={
        <div className="text-nowrap" style={{ display: "flex", flexDirection: "column" }}>
          <Space align="center" size={"small"} className="mb-03">
            <h5 style={{ width: 96, margin: 0 }}>{_t("field")}:</h5>
            <div style={{ width: 50, fontWeight: 500, fontSize: 11, color: "#b9c7ce", marginRight: 16 }}>({_t("weight")})</div>
            <div style={{ width: 50, fontWeight: 500, fontSize: 11 }}>{_t("score")}</div>
            <div style={{ width: 50, fontWeight: 500, color: "#213548", fontSize: 11 }}>{_t("weighted")}</div>
          </Space>
          <ScoreListItem label={_t("fuzzy")} score={bestMatch.result?.scores.jac} weight={weight.jaccard} />
          <ScoreListItem label={_t("year")} score={bestMatch.result?.scores.year} weight={weight.year} />
          <ScoreListItem label={_t("horse_power")} score={bestMatch.result?.scores.horsePower} weight={weight.horsePower} />
          <ScoreListItem label={_t("gearbox")} score={bestMatch.result?.scores.gearbox} weight={weight.gearbox} />
          <ScoreListItem label={_t("fuel")} score={bestMatch.result?.scores.fuelType} weight={weight.fuelType} />
          {/* <ScoreListItem label={"CO2"} score={bestMatch.result?.scores.emission} weight={weight.emission} /> */}
          <ScoreListItem label={_t("trim")} score={bestMatch.result?.scores.bodyType} weight={weight.bodyType} />
          <ScoreListItem label={_t("battery_capacity")} score={bestMatch.result?.scores.batteryCapacity} weight={weight.batteryCapacity} />
        </div>
      }
    >
      {children}
    </Popover>
  )
}


type ScoreListItemProps = {
  score: Score | undefined,
  label: string
  weight: number;
}
function ScoreListItem({ score, label, weight }: ScoreListItemProps) {
  return (
    <Space align="start" size={"small"} className="text-nowrap">
      <div style={{ width: 96, fontSize: 11 }}>{label}:</div>
      <div style={{ width: 50, fontWeight: 500, fontSize: 11, color: "#b9c7ce", marginRight: 16 }}>({format.percent(weight)})</div>
      <div style={{ width: 50, fontWeight: 500, fontSize: 11, }}>{format.percent(score?.score)}</div>
      <div style={{ width: 50, fontWeight: 500, color: "#213548", fontSize: 11 }}>{format.percent(score?.weightedScore)}</div>
    </Space>
  )
}
